const SCROLL_EPS = 2;

class CardCarouselElement extends HTMLElement {
  #abortController;
  #resizeObserver;
  #mutationObserver;

  /** @param {HTMLElement} viewport */
  #getSlides(viewport) {
    return [...viewport.querySelectorAll(".carousel__slide")];
  }

  /** @param {HTMLElement} viewport */
  #maxScroll(viewport) {
    return Math.max(0, viewport.scrollWidth - viewport.clientWidth);
  }

  /**
   * Slide starts measured from the scroll origin, so leading padding is not counted twice.
   * @param {HTMLElement[]} slides
   */
  #slideStarts(slides) {
    const base = slides[0]?.offsetLeft ?? 0;
    return slides.map((slide) => slide.offsetLeft - base);
  }

  /**
   * The last slide that starts before the current position. Stepping by direction rather
   * than by nearest slide matters at the end of the row, where the remaining scroll can be
   * shorter than half a slide and the nearest slide is the one already on screen.
   * @param {HTMLElement} viewport
   * @param {HTMLElement[]} slides
   */
  #prevIndex(viewport, slides) {
    const starts = this.#slideStarts(slides);
    const left = viewport.scrollLeft;
    for (let i = starts.length - 1; i >= 0; i--) {
      if (starts[i] < left - SCROLL_EPS) return i;
    }
    return -1;
  }

  /**
   * The first slide that starts after the current position.
   * @param {HTMLElement} viewport
   * @param {HTMLElement[]} slides
   */
  #nextIndex(viewport, slides) {
    const starts = this.#slideStarts(slides);
    const left = viewport.scrollLeft;
    for (let i = 0; i < starts.length; i++) {
      if (starts[i] > left + SCROLL_EPS) return i;
    }
    return -1;
  }

  /**
   * Move the carousel viewport only. scrollIntoView also scrolls the cart drawer,
   * so a short underlay row never actually changes slide.
   * @param {HTMLElement} viewport
   * @param {HTMLElement[]} slides
   * @param {number} index
   * @param {ScrollBehavior} behavior
   */
  #scrollToIndex(viewport, slides, index, behavior) {
    const left = this.#slideStarts(slides)[index];
    if (left == null) return;
    viewport.scrollTo({ left, behavior });
  }

  /**
   * A closed cart drawer is display:none, so both sizes are 0 and the arrows
   * would stay disabled after the cart redraw. Wait until the row is visible.
   * @param {HTMLElement} viewport
   * @param {HTMLButtonElement | null} prevBtn
   * @param {HTMLButtonElement | null} nextBtn
   */
  #syncArrows(viewport, prevBtn, nextBtn) {
    if (viewport.clientWidth === 0) return;

    const maxScroll = this.#maxScroll(viewport);
    const left = viewport.scrollLeft;
    prevBtn?.toggleAttribute("disabled", left <= SCROLL_EPS);
    nextBtn?.toggleAttribute("disabled", left >= maxScroll - SCROLL_EPS);
  }

  /** @param {ParentNode} root @param {string} carouselId */
  #syncFromDom(root, carouselId) {
    const viewport = this.querySelector(".carousel__viewport");
    if (!(viewport instanceof HTMLElement)) return;
    const prev = carouselId ? root.querySelector?.(`[data-carousel-prev="${carouselId}"]`) : null;
    const next = carouselId ? root.querySelector?.(`[data-carousel-next="${carouselId}"]`) : null;
    this.#syncArrows(
      viewport,
      prev instanceof HTMLButtonElement ? prev : null,
      next instanceof HTMLButtonElement ? next : null
    );
  }

  /**
   * Cart HTML arrives before the drawer is open. Measure again once layout exists.
   * @param {ParentNode} root
   * @param {string} carouselId
   */
  #scheduleSync(root, carouselId) {
    requestAnimationFrame(() => {
      this.#syncFromDom(root, carouselId);
      requestAnimationFrame(() => this.#syncFromDom(root, carouselId));
    });
  }

  connectedCallback() {
    const viewportNode = this.querySelector(".carousel__viewport");
    if (!viewportNode) return;

    this.disconnectedCallback();

    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    const carouselId = this.getAttribute("data-carousel-id") || "";
    const root = this.closest("[data-upsell-carousel-root]") || this.getRootNode?.() || document;

    // Clicks stay on the root so a cart morph can replace the buttons.
    root.addEventListener?.(
      "click",
      (event) => {
        const target = event.target instanceof Element ? event.target : null;
        const button = target?.closest("button");
        if (!(button instanceof HTMLButtonElement)) return;

        const viewport = this.querySelector(".carousel__viewport");
        if (!(viewport instanceof HTMLElement) || !this.contains(viewport)) return;

        const slides = this.#getSlides(viewport);
        const isPrev = Boolean(carouselId) && button.matches(`[data-carousel-prev="${carouselId}"]`);
        const isNext = Boolean(carouselId) && button.matches(`[data-carousel-next="${carouselId}"]`);
        if (!isPrev && !isNext) return;

        const index = isPrev ? this.#prevIndex(viewport, slides) : this.#nextIndex(viewport, slides);
        if (index >= 0) this.#scrollToIndex(viewport, slides, index, "smooth");
      },
      { signal }
    );

    // Scroll does not bubble; capture still reaches a replaced viewport.
    this.addEventListener("scroll", () => this.#syncFromDom(root, carouselId), {
      signal,
      capture: true,
      passive: true
    });

    document.addEventListener("cart:update", () => this.#scheduleSync(root, carouselId), { signal });

    this.closest("drawer-component")?.addEventListener("dialog:open", () => this.#scheduleSync(root, carouselId), {
      signal
    });

    this.#resizeObserver = new ResizeObserver(() => this.#scheduleSync(root, carouselId));
    this.#resizeObserver.observe(this);
    this.#resizeObserver.observe(viewportNode);

    // Cart section morphs replace the upsell cards and arrow buttons without
    // changing the drawer width. ResizeObserver does not see that change.
    this.#mutationObserver = new MutationObserver(() => this.#scheduleSync(root, carouselId));
    this.#mutationObserver.observe(root, { childList: true, subtree: true });

    this.#scheduleSync(root, carouselId);
  }

  disconnectedCallback() {
    this.#resizeObserver?.disconnect();
    this.#resizeObserver = undefined;
    this.#mutationObserver?.disconnect();
    this.#mutationObserver = undefined;
    this.#abortController?.abort();
    this.#abortController = undefined;
  }
}

if (!customElements.get("card-carousel")) {
  customElements.define("card-carousel", CardCarouselElement);
}
